import React, { createContext, useContext, useState, useEffect } from 'react';

const STORAGE_KEY = 'pathbuddy_theme';

/**
 * The three colour schemes, in the order the cycle buttons step through them.
 * "black" is a dark scheme: <html> gets data-theme="dark" (so every dark-mode
 * rule applies) plus data-tone="black", which swaps the navy backgrounds in
 * src/styles/ivori-tokens.css for near-black and neutral greys.
 */
export const THEMES = ['light', 'dark', 'black'];

export const THEME_LABELS = { light: 'Light', dark: 'Dark', black: 'Black' };

const ThemeContext = createContext(null);

function normaliseTheme(value) {
  return THEMES.includes(value) ? value : 'light';
}

export function nextTheme(theme) {
  const i = THEMES.indexOf(normaliseTheme(theme));
  return THEMES[(i + 1) % THEMES.length];
}

function persistTheme(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch (_) {}
}

function getStoredTheme() {
  try {
    return normaliseTheme(localStorage.getItem(STORAGE_KEY));
  } catch (_) {
    return 'light';
  }
}

function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-theme', theme === 'light' ? 'light' : 'dark');
  if (theme === 'black') {
    root.setAttribute('data-tone', 'black');
  } else {
    root.removeAttribute('data-tone');
  }
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    const t = getStoredTheme();
    applyTheme(t);
    return t;
  });

  // The theme is written to storage only when the visitor changes it, so
  // nothing is stored on a first visit that they did not ask for.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = (value) => {
    const next = normaliseTheme(value);
    persistTheme(next);
    setThemeState(next);
  };
  const toggleTheme = () => setTheme(nextTheme(theme));
  const isDark = theme !== 'light';

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  return ctx || { theme: 'light', setTheme: () => {}, toggleTheme: () => {}, isDark: false };
}

export default ThemeContext;
